import { typeahead } from "../types";

export const CACHE_TTL = 1000 * 60 * 15; // 15 minutes
export const DEFAULT_AUTO_CLEANUP_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
export const MAX_CACHE_SIZE = 1000; // Maximum number of entries in cache

type CacheKey = string;

type InMemoryCacheResult<T> = {
  results: T[];
  expiry: number;
};

/**
 * A simple in-memory cache that stores results for a given key.
 * The initial use cases of the cache are:
 * - Bypassing the Typeahead RXJS pipeline when user types a query that is already in the cache.
 * - Bypassing repeated SQLite or localStorage READS for large JSON objects while gathering Typeahead Sources.
 */
export class InMemoryCache<T> {
  private cache = new Map<CacheKey, InMemoryCacheResult<T>>();
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.startAutoCleanup();
  }

  /**
   * Checks if the cache has a result for the given key.
   * If the key is not found, it will return false.
   * If the key is found, it will return true if the result is not expired.
   * If the key is found and the result is expired, it will clear the result and return false.
   * Then, if clearExpired is true, all expired results will be cleared.
   */
  has(key: CacheKey, clearExpired: boolean = false): boolean {
    const hasResult = this.cache.has(key);

    if (!hasResult) return false;

    const result = this.cache.get(key) as InMemoryCacheResult<T>;

    if (this.isExpired(result)) {
      this.clearResult(key);
      return false;
    }

    if (clearExpired) {
      this.clearExpired();
    }

    return true;
  }

  /**
   * Returns the results for the given key.
   * If the key is not found, it will return undefined.
   * If the key is found and the result is expired, it will clear the result and return undefined.
   * Otherwise, it will return the results.
   */
  get(key: CacheKey): T[] | undefined {
    const result = this.cache.get(key);

    if (result && this.isExpired(result)) {
      this.clearResult(key);
      return undefined;
    }

    return result?.results;
  }

  /**
   * Sets the results for the given key.
   * The results will be valid for CACHE_TTL milliseconds.
   * If cache exceeds MAX_CACHE_SIZE, the oldest entries will be evicted first.
   */
  set(key: CacheKey, value: T[]): void {
    if (this.cache.size >= MAX_CACHE_SIZE) {
      this.evictOldestEntries();
    }

    // This ensures we maintain Map order and don't mutate a key
    if (this.cache.has(key)) {
      this.clearResult(key);
    }

    this.cache.set(key, {
      results: value,
      expiry: this.getExpiry(),
    });
  }

  clearExpired(): void {
    this.cache.forEach((value, key) => {
      if (!this.isExpired(value)) return;
      this.cache.delete(key);
    });
  }

  clearResult(key: CacheKey): void {
    this.cache.delete(key);
  }

  createKey(...args: string[]): CacheKey {
    return args.map((arg) => arg.toLowerCase()).join("-");
  }

  private getExpiry(): number {
    return Date.now() + CACHE_TTL;
  }

  private isExpired(result: InMemoryCacheResult<T>): boolean {
    return result.expiry < Date.now();
  }

  private evictOldestEntries(): void {
    const firstKey = this.cache.keys().next().value;
    if (!firstKey) return;
    this.cache.delete(firstKey);
  }

  private startAutoCleanup(): void {
    this.cleanupInterval = setInterval(() => {
      this.clearExpired();
    }, DEFAULT_AUTO_CLEANUP_INTERVAL_MS);
  }

  // Useful in unit testing
  // Electron/browser will terminate the process when the window is closed
  // so we don't need to manually stop the cleanup interval
  dispose(): void {
    if (!this.cleanupInterval) return;
    clearInterval(this.cleanupInterval);
    this.cleanupInterval = null;
  }

  flush(): void {
    this.cache.clear();
  }
}

export const pipelineInMemoryCache =
  new InMemoryCache<typeahead.ScoredResult>();
